import type { Operation } from '@apollo/client';

const OPERATION_NAME_PARAM = 'op';

type UriResolver = (operation: Operation) => string;
type BatchKeyFn = (operation: Operation) => string;

// Reconstructs the original relative URL format with updated search/hash from the parsed URL.
// The caller is responsible for stripping any placeholder base — this function only
// combines the original path prefix with the parsed query string and hash.
const rebuildRelativeUrl = (baseUrl: string, search: string, hash: string) => {
  const suffix = `${search}${hash}`;

  if (baseUrl.startsWith('//')) {
    const withoutProtocol = baseUrl.split('?')[0].split('#')[0];
    return `${withoutProtocol}${suffix}`;
  }

  const basePath = baseUrl.split('?')[0].split('#')[0];
  return `${basePath}${suffix}`;
};

const applyOperationParam = (url: URL, operationName?: string) => {
  if (operationName) {
    url.searchParams.set(OPERATION_NAME_PARAM, operationName);
  } else {
    url.searchParams.delete(OPERATION_NAME_PARAM);
  }
};

// Cascading URL construction: try absolute URL, then relative with placeholder base,
// then raw string concatenation. Each catch falls through to the next strategy.
const buildUrl = (baseUrl: string, operationName?: string) => {
  try {
    const url = new URL(baseUrl);
    applyOperationParam(url, operationName);
    return url.toString();
  } catch {
    try {
      // Relative URLs (e.g. "/graphql", "../graphql") aren't valid on their own.
      // We use a disposable base (.invalid is IANA-reserved) purely to enable
      // URL parsing for search param manipulation. Only the search and hash are
      // kept from the parsed result; the base domain is discarded here explicitly.
      const url = new URL(baseUrl, 'http://url-parse-base.invalid');
      applyOperationParam(url, operationName);
      return rebuildRelativeUrl(baseUrl, url.search, url.hash);
    } catch {
      if (!operationName) {
        return baseUrl;
      }

      // url.searchParams.set() auto-encodes in the paths above.
      // This manual fallback only runs if both URL constructors fail.
      const separator = baseUrl.includes('?') ? '&' : '?';
      return `${baseUrl}${separator}${OPERATION_NAME_PARAM}=${encodeURIComponent(operationName)}`;
    }
  }
};

const createBatchKeyForOperation = (base: string): BatchKeyFn => {
  return (operation: Operation) => {
    const context = operation.getContext();
    // Exclude per-request dynamic headers (like x-joy-request-id) from the batch key.
    // Including them would give every operation a unique key, defeating batching.
    const { 'x-joy-request-id': _requestId, ...stableHeaders } = context.headers || {};
    const contextConfig = {
      http: context.http,
      options: context.fetchOptions,
      credentials: context.credentials,
      headers: stableHeaders,
    };

    return `${base}${JSON.stringify(contextConfig)}`;
  };
};

export const createOperationAwareUri = (baseUrl: string) => {
  const base = buildUrl(baseUrl);

  const resolve: UriResolver = (operation: Operation) => {
    const { operationName } = operation;
    return operationName ? buildUrl(baseUrl, operationName) : base;
  };

  const batchKey = createBatchKeyForOperation(base);

  return { resolve, batchKey };
};
